import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
} from '@mui/material';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import type { DeviceMetricsResponse, MetricsTimeRange } from '../api/JsdhNotifikaceService/generated/models/DeviceMetrics';

// ===== Time Range Selector =====
interface TimeRangeSelectorProps {
    value: MetricsTimeRange;
    onChange: (value: MetricsTimeRange) => void;
}

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({ value, onChange }) => {
    return (
        <ToggleButtonGroup
            value={value}
            exclusive
            onChange={(_, newValue) => newValue && onChange(newValue)}
            size="small"
        >
            <ToggleButton value="24h">24 hodin</ToggleButton>
            <ToggleButton value="7d">7 dni</ToggleButton>
            <ToggleButton value="30d">30 dni</ToggleButton>
        </ToggleButtonGroup>
    );
};

// ===== Helper Components =====
const NoDataMessage: React.FC = () => (
    <Box display="flex" alignItems="center" justifyContent="center" height={200}>
        <Typography color="text.secondary">Zadna data k zobrazeni</Typography>
    </Box>
);

const formatTimestamp = (ts: string, timeRange: MetricsTimeRange): string => {
    const date = new Date(ts);
    if (timeRange === '24h') {
        return date.toLocaleTimeString('cs-CZ', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    return date.toLocaleDateString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// ===== Temperature Chart (CPU + GPU) =====
interface TemperatureChartProps {
    data: DeviceMetricsResponse[];
    timeRange: MetricsTimeRange;
}

export const TemperatureChart: React.FC<TemperatureChartProps> = ({ data, timeRange }) => {
    const theme = useTheme();

    const chartData = useMemo(() => {
        return data
            .map((m) => ({
                ts: formatTimestamp(m.ts, timeRange),
                cpuTemp: m.cpuTempC,
                gpuTemp: m.gpuTempC,
            }))
            .reverse();
    }, [data, timeRange]);

    if (data.length === 0) {
        return <NoDataMessage />;
    }

    return (
        <Box height={250}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="ts"
                        fontSize={10}
                        tick={{ fill: theme.palette.text.secondary }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        unit="°C"
                        fontSize={12}
                        tick={{ fill: theme.palette.text.secondary }}
                        domain={['auto', 'auto']}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.divider,
                        }}
                        formatter={(value) => typeof value === 'number' ? `${value.toFixed(1)} °C` : '-'}
                    />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="cpuTemp"
                        name="CPU"
                        stroke={theme.palette.error.main}
                        dot={false}
                        strokeWidth={2}
                    />
                    <Line
                        type="monotone"
                        dataKey="gpuTemp"
                        name="GPU"
                        stroke={theme.palette.warning.main}
                        dot={false}
                        strokeWidth={2}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

// ===== CPU Load Chart =====
interface CpuLoadChartProps {
    data: DeviceMetricsResponse[];
    timeRange: MetricsTimeRange;
}

export const CpuLoadChart: React.FC<CpuLoadChartProps> = ({ data, timeRange }) => {
    const theme = useTheme();

    const chartData = useMemo(() => {
        return data
            .map((m) => ({
                ts: formatTimestamp(m.ts, timeRange),
                cpuPercent: m.cpuPercent,
                load1: m.load1,
                load5: m.load5,
                load15: m.load15,
            }))
            .reverse();
    }, [data, timeRange]);

    if (data.length === 0) return <NoDataMessage />;

    // Determine max load for right Y-axis domain
    const maxLoad = Math.max(
        ...chartData.map((d) => Math.max(d.load1 || 0, d.load5 || 0, d.load15 || 0))
    );
    const loadDomain = [0, Math.max(Math.ceil(maxLoad * 1.2), 4)];

    return (
        <Box height={250}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="ts"
                        fontSize={10}
                        tick={{ fill: theme.palette.text.secondary }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        yAxisId="left"
                        unit="%"
                        domain={[0, 100]}
                        fontSize={12}
                        tick={{ fill: theme.palette.text.secondary }}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={loadDomain}
                        fontSize={12}
                        tick={{ fill: theme.palette.text.secondary }}
                        label={{ value: 'Load', angle: 90, position: 'insideRight', fontSize: 10 }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.divider,
                        }}
                        formatter={(value, name) => {
                            if (typeof value !== 'number') return '-';
                            if (name === 'CPU %') return `${value.toFixed(1)} %`;
                            return value.toFixed(2);
                        }}
                    />
                    <Legend />
                    <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="cpuPercent"
                        name="CPU %"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.light}
                        fillOpacity={0.3}
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="load1"
                        name="Load 1m"
                        stroke={theme.palette.error.main}
                        dot={false}
                        strokeWidth={1.5}
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="load5"
                        name="Load 5m"
                        stroke={theme.palette.warning.main}
                        dot={false}
                        strokeWidth={1.5}
                    />
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="load15"
                        name="Load 15m"
                        stroke={theme.palette.success.main}
                        dot={false}
                        strokeWidth={1.5}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
};

// ===== Memory Chart =====
interface MemoryChartProps {
    data: DeviceMetricsResponse[];
    timeRange: MetricsTimeRange;
}

export const MemoryChart: React.FC<MemoryChartProps> = ({ data, timeRange }) => {
    const theme = useTheme();

    const chartData = useMemo(() => {
        return data
            .map((m) => ({
                ts: formatTimestamp(m.ts, timeRange),
                used: m.memUsedMb,
                total: m.memTotalMb,
                percent: m.memUsedPercent,
            }))
            .reverse();
    }, [data, timeRange]);

    if (data.length === 0) return <NoDataMessage />;

    return (
        <Box height={250}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="ts"
                        fontSize={10}
                        tick={{ fill: theme.palette.text.secondary }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        unit="%"
                        domain={[0, 100]}
                        fontSize={12}
                        tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.divider,
                        }}
                        formatter={(value, name) => {
                            if (typeof value !== 'number') return '-';
                            if (name === 'RAM %') return `${value.toFixed(1)} %`;
                            return `${value.toFixed(0)} MB`;
                        }}
                    />
                    <Legend />
                    <Area
                        type="monotone"
                        dataKey="percent"
                        name="RAM %"
                        stroke={theme.palette.success.main}
                        fill={theme.palette.success.light}
                        fillOpacity={0.3}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};

// ===== Disk Chart =====
interface DiskChartProps {
    data: DeviceMetricsResponse[];
    timeRange: MetricsTimeRange;
}

export const DiskChart: React.FC<DiskChartProps> = ({ data, timeRange }) => {
    const theme = useTheme();

    const chartData = useMemo(() => {
        return data
            .map((m) => ({
                ts: formatTimestamp(m.ts, timeRange),
                percent: m.diskUsedPercent,
                usedGb: m.diskUsedGb,
                totalGb: m.diskTotalGb,
            }))
            .reverse();
    }, [data, timeRange]);

    if (data.length === 0) return <NoDataMessage />;

    return (
        <Box height={250}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="ts"
                        fontSize={10}
                        tick={{ fill: theme.palette.text.secondary }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        unit="%"
                        domain={[0, 100]}
                        fontSize={12}
                        tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.divider,
                        }}
                        formatter={(value, name) => {
                            if (typeof value !== 'number') return '-';
                            if (name === 'Disk %') return `${value.toFixed(1)} %`;
                            return `${value.toFixed(1)} GB`;
                        }}
                    />
                    <Legend />
                    <Area
                        type="monotone"
                        dataKey="percent"
                        name="Disk %"
                        stroke={theme.palette.info.main}
                        fill={theme.palette.info.light}
                        fillOpacity={0.3}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};

// ===== WiFi Signal Chart =====
interface WifiSignalChartProps {
    data: DeviceMetricsResponse[];
    timeRange: MetricsTimeRange;
}

export const WifiSignalChart: React.FC<WifiSignalChartProps> = ({ data, timeRange }) => {
    const theme = useTheme();

    const chartData = useMemo(() => {
        return data
            .map((m) => ({
                ts: formatTimestamp(m.ts, timeRange),
                signal: m.wifiSignalDbm,
            }))
            .reverse();
    }, [data, timeRange]);

    // Skip if no WiFi data
    const hasWifiData = chartData.some((d) => d.signal !== null);
    if (!hasWifiData) return null;

    return (
        <Box height={250}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="ts"
                        fontSize={10}
                        tick={{ fill: theme.palette.text.secondary }}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        unit=" dBm"
                        domain={[-100, -30]}
                        fontSize={12}
                        tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.divider,
                        }}
                        formatter={(value) => typeof value === 'number' ? `${value} dBm` : '-'}
                    />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="signal"
                        name="WiFi"
                        stroke={theme.palette.secondary.main}
                        dot={false}
                        strokeWidth={2}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};
